import React, { useMemo } from 'react'

import { StockCellContent } from '@components/common/StockCellContent'
import { TableCell } from '@components/common/TableCell'
import { Tooltip } from '@components/common/Tooltip'
import { useDashboardPermissions } from '@components/utils/DashboardPermissions'
import { CheckboxIndeterminate, TableSortableUI, TableUI } from '@fourthwall/components'
import type { ColumnDef } from '@tanstack/react-table'
import { insertIf } from '@utils/insertIf'

import { ProductStockVariantsTooltipContent } from '../../../components/ProductStockVariantsTooltipContent/ProductStockVariantsTooltipContent'
import { ProductThumbnailWithTooltip } from '../../../components/ProductThumbnailWithTooltip'
import type { Product } from '../../../types'
import { ProductStatus } from '../../ProductStatus/ProductStatus'
import * as S from '../ProductsData.styled'
import { ProductName } from '../components/ProductName/ProductName'

export const useProductsTableColumns = ({
  onArchiveSelectedOffersClick,
  onToggleMultipleOfferStatus,
}: {
  onArchiveSelectedOffersClick: (offerIds: string[]) => void
  onToggleMultipleOfferStatus: (offerIds: string[]) => void
}) => {
  const { hasPermission } = useDashboardPermissions()

  return useMemo<ColumnDef<Product>[]>(
    () => [
      {
        id: 'select-offers-active',
        header: ({ table }) => (
          <TableUI.HeaderRoot>
            <TableUI.HeaderSelectionWrapper visible>
              <CheckboxIndeterminate
                id="select-all"
                checked={table.getIsAllRowsSelected()}
                indeterminate={table.getIsSomeRowsSelected()}
                onChange={table.getToggleAllRowsSelectedHandler()}
              />
            </TableUI.HeaderSelectionWrapper>

            <TableUI.HeaderActions>
              <div style={{ width: '80px' }}>
                <TableUI.HeaderActionsHeading>
                  {table.getSelectedRowModel().flatRows.length} selected
                </TableUI.HeaderActionsHeading>
              </div>

              {table
                .getSelectedRowModel()
                .flatRows.every((row) => row.original.status !== 'ARCHIVED') && (
                <TableUI.HeaderActionsItems>
                  <TableUI.HeaderActionsItem
                    onClick={() =>
                      onToggleMultipleOfferStatus(
                        table.getSelectedRowModel().flatRows.map((row) => row.original.id),
                      )
                    }
                  >
                    Change status
                  </TableUI.HeaderActionsItem>
                  <TableUI.HeaderActionsItem
                    onClick={() =>
                      onArchiveSelectedOffersClick(
                        table.getSelectedRowModel().flatRows.map((row) => row.original.id),
                      )
                    }
                  >
                    Archive
                  </TableUI.HeaderActionsItem>
                </TableUI.HeaderActionsItems>
              )}
            </TableUI.HeaderActions>
          </TableUI.HeaderRoot>
        ),
        columns: [
          {
            id: 'row-left-side-placeholder',
            header: ({ table }) => (
              <TableUI.HeaderSelectionWrapper
                visible={table.getIsSomeRowsSelected() || table.getIsAllRowsSelected()}
              >
                <CheckboxIndeterminate
                  id="select-all"
                  checked={table.getIsAllRowsSelected()}
                  indeterminate={table.getIsSomeRowsSelected()}
                  onChange={table.getToggleAllRowsSelectedHandler()}
                />
              </TableUI.HeaderSelectionWrapper>
            ),
            cell: ({ table, row }) => {
              return (
                <>
                  {table.getIsSomeRowsSelected() || table.getIsAllRowsSelected() ? null : (
                    <TableSortableUI.RowDragHandle />
                  )}

                  <TableUI.RowSelection
                    visible={table.getIsSomeRowsSelected() || table.getIsAllRowsSelected()}
                  >
                    <CheckboxIndeterminate
                      checked={row.getIsSelected()}
                      disabled={!row.getCanSelect()}
                      indeterminate={row.getIsSomeSelected()}
                      onChange={row.getToggleSelectedHandler()}
                    />
                  </TableUI.RowSelection>
                </>
              )
            },
            meta: {
              width: '0px',
              height: '100%',
              position: 'relative',
            },
          },
          {
            id: 'photo',
            header: () => <TableUI.HeaderHeading>Product</TableUI.HeaderHeading>,
            accessorFn: (offer) => offer,
            cell: (info) => {
              const offer = info.getValue<Product>()

              return (
                <TableUI.CellItemPositioner position="start" width="60px" height="80px">
                  <ProductThumbnailWithTooltip product={offer} size="large" />
                </TableUI.CellItemPositioner>
              )
            },
            meta: {
              width: '60px',
              textAlign: 'left',
            },
          },
          {
            id: 'name',
            header: () => null,
            accessorFn: (offer) => offer,
            cell: (info) => {
              const offer = info.getValue<Product>()

              return (
                <S.ProductName>
                  <ProductName product={{ name: offer.name, type: offer.type }} />
                </S.ProductName>
              )
            },
            meta: {
              textAlign: 'left',
              width: '650px',
            },
          },
          {
            id: 'status',
            header: () => <TableUI.HeaderHeading>Status</TableUI.HeaderHeading>,
            accessorFn: (offer) => offer,
            cell: (info) => {
              const offer = info.getValue<Product>()

              return (
                <S.ProductStatus>
                  <ProductStatus status={offer.status} soldOut={offer.markedAsSoldOut} />
                </S.ProductStatus>
              )
            },
            meta: {
              textAlign: 'left',
              width: '200px',
            },
          },
          ...insertIf<ColumnDef<Product>>(hasPermission('contributions.profit'), {
            id: 'sold',
            header: () => <TableUI.HeaderHeading>Sold</TableUI.HeaderHeading>,
            accessorKey: 'sold',
            // @ts-ignore
            cell: (info) => <S.ProductSold>{info.getValue()}</S.ProductSold>,
            meta: {
              textAlign: 'left',
              width: '150px',
            },
          }),
          {
            id: 'inventory',
            header: <TableUI.HeaderHeading>In stock</TableUI.HeaderHeading>,
            accessorFn: (offer) => offer,
            cell: (info) => {
              const offer = info.getValue<Product>()

              const getAdditionalContent = () => {
                if (offer.markedAsSoldOut) {
                  return 'Marked as sold out'
                }

                if (offer.stock.type === 'UNLIMITED' || offer.stock.variants.length <= 1) {
                  return null
                }

                return (
                  <Tooltip
                    size="small"
                    content={<ProductStockVariantsTooltipContent variants={offer.stock.variants} />}
                    placement="bottom-end"
                  >
                    {offer.stock.variants.length} variants
                  </Tooltip>
                )
              }

              return (
                <S.ProductInventory>
                  <TableCell
                    mainContent={
                      <StockCellContent type={offer.stock.type} quantity={offer.stock.quantity} />
                    }
                    additionalContent={getAdditionalContent()}
                  />
                </S.ProductInventory>
              )
            },
            meta: {
              textAlign: 'left',
              width: '180px',
            },
          },
        ],
        meta: {
          textAlign: 'left',
        },
      },
    ],
    [hasPermission, onArchiveSelectedOffersClick, onToggleMultipleOfferStatus],
  )
}
